import * as Yup from 'yup';

/**
 * Sprint 47, story-233 (Trello 0FJwCeJe). The ONE password rule for every web form and checklist.
 *
 * Cause of the defect this replaces: each form carried its own copy of an allowlist regex (a
 * fixed set of eight symbols) that disagreed with Cognito, which accepts any symbol. A user typing
 * `Abcdef1.` saw every checklist row met while the form silently refused to submit. The checklist
 * rows and the validators are now derived from the same array, so they cannot disagree.
 *
 * Rule set (identical on mobile, `redesign/src/utils/validation/passwordPolicy.ts`): at least 8
 * characters (at most 256), an uppercase letter, a lowercase letter, a digit, at least one
 * non-alphanumeric character (any symbol, or a space that is not at an edge), and no leading or
 * trailing space. Cognito stays the final judge; its message is shown verbatim on rejection
 * (decision D2). The password is never trimmed, escaped or otherwise transformed.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 256;

export type PasswordRuleKey = 'length' | 'uppercase' | 'lowercase' | 'number' | 'symbol' | 'edges';

export interface PasswordPolicyRule {
  key: PasswordRuleKey;
  /** Key in the `passwordPolicy` locale section for the checklist row. */
  labelKey: PasswordRuleKey;
  /** Key in the `passwordPolicy` locale section for the field error sentence. */
  errorKey: `${PasswordRuleKey}Error`;
  test: (value: string) => boolean;
}

export const PASSWORD_POLICY_RULES: PasswordPolicyRule[] = [
  {
    key: 'length',
    labelKey: 'length',
    errorKey: 'lengthError',
    test: (value) => value.length >= PASSWORD_MIN_LENGTH,
  },
  {
    key: 'uppercase',
    labelKey: 'uppercase',
    errorKey: 'uppercaseError',
    test: (value) => /[A-Z]/.test(value),
  },
  {
    key: 'lowercase',
    labelKey: 'lowercase',
    errorKey: 'lowercaseError',
    test: (value) => /[a-z]/.test(value),
  },
  {
    key: 'number',
    labelKey: 'number',
    errorKey: 'numberError',
    test: (value) => /\d/.test(value),
  },
  {
    key: 'symbol',
    labelKey: 'symbol',
    errorKey: 'symbolError',
    // Any non-alphanumeric character counts, including a space. This accepts every Cognito
    // symbol: ^ $ * . [ ] { } ( ) ? " ! @ # % & / \ , > < ' : ; | _ ~ ` = + -
    test: (value) => /[^A-Za-z0-9]/.test(value),
  },
  {
    key: 'edges',
    labelKey: 'edges',
    errorKey: 'edgesError',
    // Unmet on an empty field so an empty field never shows a green row.
    test: (value) => value.length > 0 && !/^\s|\s$/.test(value),
  },
];

/** Strings for the checklist and field errors: the `passwordPolicy` locale section. */
export type PasswordPolicyMessages = {
  [K in PasswordRuleKey | `${PasswordRuleKey}Error`]: string;
} & {
  tooLong: string;
  required: string;
};

export const isPasswordTooLong = (value: string): boolean => value.length > PASSWORD_MAX_LENGTH;

/** The first rule the value fails, in checklist order, or undefined when every rule is met. */
export const firstFailingRule = (value: string): PasswordPolicyRule | undefined =>
  PASSWORD_POLICY_RULES.find((rule) => !rule.test(value));

/** True when the value satisfies every rule and is within the maximum length. */
export const isPasswordValid = (value: string): boolean =>
  !isPasswordTooLong(value) && firstFailingRule(value) === undefined;

/**
 * Yup schema built from the same rules as the checklist. The field error is the first failing
 * rule's sentence, so a user sees one short instruction at a time.
 */
export const buildPasswordSchema = (
  messages: Pick<PasswordPolicyMessages, 'tooLong' | 'required' | `${PasswordRuleKey}Error`>,
  requiredMessage?: string,
) =>
  Yup.string()
    .required(requiredMessage ?? messages.required)
    .test('password-max-length', messages.tooLong, (value) => !value || !isPasswordTooLong(value))
    .test('password-policy', function validate(value) {
      if (!value) return true;
      const failing = firstFailingRule(value);
      return failing ? this.createError({ message: messages[failing.errorKey] }) : true;
    });
